import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    enterRatio: { type: Number, default: 0.6 },
    exitRatio:  { type: Number, default: 0.4 },
    videoSelector: {type:String, default: "video"},
    rootSelector: {type:String, default: "infotainment-slider"},
    resetOnPause: { type: Boolean, default: false }
  }

  connect() {
    this.root = this.element.closest(this.rootSelectorValue)
    this.video = this.element.querySelector(this.videoSelectorValue)
    if (!this.video) return

    this.ENTER = this.enterRatioValue
    this.EXIT  = this.exitRatioValue
    this.inView = false

    this.onIntersectHandle = this.#onIntersect.bind(this)
    this.outerIntersectHandle = this.#onOuterIntersect.bind(this)
    this.onVisibilityChangeHandle = this.#onVisibilityChange.bind(this)

    this.io = new IntersectionObserver(this.onIntersectHandle, {
      root: this.root,
      threshold: [this.ENTER, this.EXIT]
    })
    this.io.observe(this.element)

    this.outerIo = new IntersectionObserver(this.outerIntersectHandle, {
      root: null,
      threshold: [0,1],
    })
    this.outerIo.observe(this.root)

    document.addEventListener("visibilitychange", this.onVisibilityChangeHandle)
    window.addEventListener("pagehide", this.onVisibilityChangeHandle)
  }

  disconnect() {
    this.io.disconnect()
    this.outerIo.disconnect()
    document.removeEventListener("visibilitychange", this.onVisibilityChangeHandle)
    window.removeEventListener("pagehide", this.onVisibilityChangeHandle)
    this.#pause()
  }

  // *************************************
  // HANDLERS
  // *************************************
  async #onIntersect(entries) {
    const entry = entries[0]
    await this.#applyIntersect({
      entry,
      enter: this.ENTER,
      exit:  this.EXIT,
      announce: true
    })
  }

  async #onOuterIntersect(entries) {
    const entry = entries[0]
    await this.#applyIntersect({
      entry,
      enter: 1,
      exit:  0,
    })
  }

  async #applyIntersect({ entry, enter, exit}) {
    if (!entry) return
    const ratio = Math.max(0, Math.min(1, entry.intersectionRatio ?? 0))
    const isIn   = entry.isIntersecting

    const shouldEnter = !this.inView && isIn && ratio >= enter
    const shouldExit  =  this.inView && (!isIn || ratio <= exit)

    if (shouldEnter) {
      this.inView = true
      await this.#play()
      return
    }

    if (shouldExit) {
      this.inView = false
      this.#pause()
    }
  }

  #onVisibilityChange() {
    if (document.hidden) this.#pause()
  }

  // *************************************
  // VIDEO ACTIONS
  // *************************************
  async #play() {
    if (!this.video) return
    try { await this.video.play() } catch (_) {}
  }

  #pause() {
    if (!this.video) return
    try { this.video.pause() } catch (_) {}
    if (this.resetOnPauseValue) this.video.currentTime = 0
  }
}